/**
 * Simple modal for company admins to schedule assessment events on a draft cycle
 * before it can be released to managers.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { Plus, Trash2, X } from "../icons";
import { cb, cbCn } from "./cycleBuilderTheme";
import { resolveDisplayedCycleMonths } from "./formatCycleDuration";

interface DraftEvent {
  event_name: string;
  offset_months_open: number;
  offset_months_close: number;
  sort_order: number;
}

export function AddAssessmentEventsModal({
  open,
  cycleId,
  cycleTitle,
  expiryYears,
  expiryMonths,
  currentUserEmail,
  companyCode,
  isDarkMode = false,
  isMobile = false,
  onClose,
  onSaved,
}: {
  open: boolean;
  cycleId: number | null;
  cycleTitle: string;
  expiryYears?: number;
  expiryMonths?: number | null;
  currentUserEmail?: string | null;
  companyCode?: string;
  isDarkMode?: boolean;
  isMobile?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const durationInMonths = resolveDisplayedCycleMonths({ expiry_years: expiryYears, expiry_months: expiryMonths });
  const [events, setEvents] = useState<DraftEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputClass = cbCn(
    "mt-1 w-full rounded-lg border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500",
    cb.input(isDarkMode)
  );

  const loadEvents = useCallback(async () => {
    if (!open || cycleId == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${cycleId}/events?v=${Date.now()}`, {
        cache: "no-store",
        headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        setError(typeof data?.error === "string" ? data.error : translateUi("Could not load assessment events."));
        setEvents([]);
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      if (rows.length === 0) {
        setEvents([
          {
            event_name: "",
            offset_months_open: 0,
            offset_months_close: Math.min(1, durationInMonths),
            sort_order: 0,
          },
        ]);
      } else {
        setEvents(
          rows.map((row: Record<string, unknown>, i: number) => ({
            event_name: String(row.event_name ?? ""),
            offset_months_open: Number(row.offset_months_open ?? 0),
            offset_months_close: Number(row.offset_months_close ?? 0),
            sort_order: Number(row.sort_order ?? i),
          }))
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not load assessment events."));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [open, cycleId, currentUserEmail, durationInMonths]);

  useEffect(() => {
    if (open) void loadEvents();
    else {
      setEvents([]);
      setError(null);
    }
  }, [open, loadEvents]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const monthOptions = useMemo(() => {
    const opts: number[] = [];
    for (let m = 0; m <= durationInMonths; m++) opts.push(m);
    return opts;
  }, [durationInMonths]);

  const monthLabel = (m: number) => (m === 0 ? translateUi("Month 0 (from start)") : `Month ${m}`);

  const eventErrors = useMemo(() => {
    const errors: Record<number, string> = {};
    events.forEach((ev, i) => {
      const openM = Number(ev.offset_months_open);
      const closeM = Number(ev.offset_months_close);
      if (!ev.event_name?.trim()) errors[i] = translateUi("Title is required");
      else if (!Number.isFinite(openM) || openM < 0 || openM > durationInMonths)
        errors[i] = translateUi("Start month must be between 0 and {{max}}", { max: durationInMonths });
      else if (!Number.isFinite(closeM) || closeM < 0 || closeM > durationInMonths)
        errors[i] = translateUi("End month must be between 0 and {{max}}", { max: durationInMonths });
      else if (closeM < openM) errors[i] = translateUi("End month must be on or after start month");
    });
    return errors;
  }, [events, durationInMonths]);

  const allEventsValid = events.length > 0 && Object.keys(eventErrors).length === 0;

  const addEvent = () => {
    const prev = events.length > 0 ? events[events.length - 1] : null;
    const startFromPrev = prev
      ? Math.min(durationInMonths, Math.max(0, Number(prev.offset_months_close) || 0))
      : 0;
    const endDefault = prev ? startFromPrev : Math.min(1, durationInMonths);
    setEvents((prevEvents) => [
      ...prevEvents,
      {
        event_name: "",
        offset_months_open: startFromPrev,
        offset_months_close: endDefault,
        sort_order: prevEvents.length,
      },
    ]);
  };

  const updateEvent = (index: number, field: keyof DraftEvent, value: string | number) => {
    setEvents((prevEvents) =>
      prevEvents.map((ev, i) => (i === index ? { ...ev, [field]: value } : ev))
    );
  };

  const removeEvent = (index: number) => {
    setEvents((prevEvents) => prevEvents.filter((_, i) => i !== index).map((ev, i) => ({ ...ev, sort_order: i })));
  };

  const handleSave = async () => {
    if (!allEventsValid || cycleId == null || !currentUserEmail) return;
    setSaving(true);
    setError(null);
    try {
      const payload = events.map((ev, i) => ({
        event_name: ev.event_name.trim(),
        offset_months_open: Number(ev.offset_months_open),
        offset_months_close: Number(ev.offset_months_close),
        sort_order: i,
      }));
      const res = await fetch(`/api/cycles/${cycleId}/events/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          ...(companyCode ? { "X-Company-Code": companyCode } : {}),
        },
        body: JSON.stringify({ events: payload, companyCode, company_code: companyCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.error === "string" ? data.error : translateUi("Could not save assessment events."));
        return;
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not save assessment events."));
    } finally {
      setSaving(false);
    }
  };

  if (!open || cycleId == null) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={() => {
        if (!saving) onClose();
      }}
      role="presentation"
    >
      <div
        className={cbCn(
          "relative flex max-h-[min(92vh,900px)] w-full flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:max-w-4xl sm:rounded-2xl",
          isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-events-title"
      >
        <header
          className={cbCn(
            "relative shrink-0 border-b px-4 py-4 sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={cbCn(
              "absolute right-3 top-3 rounded-lg p-2",
              isDarkMode ? "text-slate-400 hover:bg-slate-800 hover:text-slate-200" : "text-slate-500 hover:bg-slate-100"
            )}
            aria-label={translateUi("Close")}
          >
            <X className="h-5 w-5" />
          </button>
          <p className={cbCn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-amber-300" : "text-amber-700")}>
            {translateUi("Assessment events")}
          </p>
          <h2 id="add-events-title" className={cbCn("mt-0.5 text-lg font-semibold pr-8", isDarkMode ? "text-slate-100" : "text-slate-900")}>
            {cycleTitle}
          </h2>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {translateUi(
              "Schedule when each assessment opens and closes within the {{months}}-month cycle. Save at least one event before you can release this cycle to managers.",
              { months: durationInMonths }
            )}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {error ? (
            <div className={cbCn("mb-4 rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))}>{error}</div>
          ) : null}
          {loading ? (
            <p className={cbCn("py-8 text-center text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading…")}</p>
          ) : (
            <div className="space-y-4">
              {events.map((ev, i) => (
                <div
                  key={i}
                  className={cbCn(
                    "rounded-lg border p-4",
                    eventErrors[i]
                      ? isDarkMode
                        ? "border-amber-500/40 bg-amber-950/30"
                        : "border-amber-300 bg-amber-50/50"
                      : isDarkMode
                        ? "border-slate-600 bg-slate-800/50"
                        : "border-slate-200 bg-slate-50/30"
                  )}
                >
                  <div
                    className={cbCn(
                      "grid gap-3",
                      isMobile ? "grid-cols-1" : "grid-cols-[minmax(0,1fr)_12.5rem_12.5rem_auto] items-end"
                    )}
                  >
                    <div className="min-w-0">
                      <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Title")}</label>
                      <input
                        type="text"
                        value={ev.event_name}
                        onChange={(e) => updateEvent(i, "event_name", e.target.value)}
                        placeholder={translateUi("e.g. Practical Ride Assessment")}
                        className={inputClass}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Start month")}</label>
                      <select
                        value={ev.offset_months_open}
                        onChange={(e) => updateEvent(i, "offset_months_open", Number(e.target.value))}
                        className={inputClass}
                      >
                        {monthOptions.map((m) => (
                          <option key={m} value={m}>
                            {monthLabel(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("End month")}</label>
                      <select
                        value={ev.offset_months_close}
                        onChange={(e) => updateEvent(i, "offset_months_close", Number(e.target.value))}
                        className={inputClass}
                      >
                        {monthOptions.map((m) => (
                          <option key={m} value={m}>
                            {monthLabel(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEvent(i)}
                      disabled={events.length <= 1}
                      className={cbCn(
                        "rounded p-2 disabled:cursor-not-allowed disabled:opacity-40",
                        isMobile ? "justify-self-end" : "mb-0.5",
                        isDarkMode
                          ? "text-slate-400 hover:bg-red-950/50 hover:text-red-300"
                          : "text-slate-500 hover:bg-red-50 hover:text-red-600"
                      )}
                      title={translateUi("Remove event")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {eventErrors[i] ? <p className={cbCn("mt-2 text-sm", cb.hint(isDarkMode))}>{eventErrors[i]}</p> : null}
                </div>
              ))}
              <button
                type="button"
                onClick={addEvent}
                className={cbCn(
                  "inline-flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-semibold",
                  isDarkMode
                    ? "border-indigo-400 bg-indigo-950/50 text-indigo-200 hover:bg-indigo-900/70"
                    : "border-indigo-500 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                )}
              >
                <Plus className="h-4 w-4" />
                {translateUi("Add event")}
              </button>
            </div>
          )}
        </div>

        <footer
          className={cbCn(
            "flex shrink-0 flex-col gap-2 border-t px-4 py-4 sm:flex-row sm:justify-end sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={cbCn("rounded-lg px-4 py-2 text-sm font-semibold", cb.btnSecondary(isDarkMode))}
          >
            {translateUi("Cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!allEventsValid || saving || loading}
            className={cbCn("rounded-lg px-4 py-2 text-sm font-semibold", cb.btnPrimary(!allEventsValid || saving || loading))}
          >
            {saving ? translateUi("Saving…") : translateUi("Save events")}
          </button>
        </footer>
      </div>
    </div>
  );
}
